<template>
  <div>
    <div v-if="loading" class="row"><span class="spinner" aria-hidden="true"></span><span class="muted small">{{ t("common.loading") }}</span></div>
    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)">
      <div class="notice__body">{{ error }}</div>
    </div>

    <template v-if="app">
      <div class="page-head">
        <div>
          <p class="small"><router-link to="/apps">← {{ t("nav.apps") }}</router-link></p>
          <h1 class="page-head__title">{{ app.name }}</h1>
          <p class="page-head__lede mono small">{{ app.handle }}</p>
        </div>
        <span :class="['badge', statusBadge]">{{ statusLabel(app.status) }}</span>
      </div>

      <div v-if="app.status === 'blocked' || app.status === 'suspended'" class="notice notice--danger" style="margin-bottom: var(--space-4)">
        <div class="notice__body">
          <p class="strong">{{ t("editor.blockedTitle") }}</p>
          <p v-if="app.blocked_reason" class="small">{{ app.blocked_reason }}</p>
        </div>
      </div>

      <nav class="tabs" role="tablist">
        <button
          v-for="key in TABS"
          :key="key"
          type="button"
          role="tab"
          :aria-selected="tab === key"
          :class="['tabs__tab', { 'tabs__tab--on': tab === key }]"
          @click="setTab(key)"
        >
          {{ t(`editor.tabs.${key}`) }}
        </button>
      </nav>

      <OverviewPanel v-if="tab === 'overview'" :app="app" @changed="load" />
      <VersionsPanel
        v-else-if="tab === 'versions'"
        :app="app"
        :versions="versions"
        :scopes="scopes"
        :listing-en="listings.en"
        @changed="load"
        @goto="setTab"
      />
      <ListingPanel v-else-if="tab === 'listing'" :app-id="app.id" :listings="listings" @saved="onListingSaved" />
      <DistributionPanel v-else-if="tab === 'distribution'" :app-id="app.id" />
      <CredentialsPanel v-else-if="tab === 'credentials'" :app="app" :credentials="credentials" @changed="reloadCredentials" />
      <TestersPanel v-else-if="tab === 'testers'" :app-id="app.id" />
      <UsagePanel v-else-if="tab === 'usage'" :app-id="app.id" />
      <InstallsPanel v-else-if="tab === 'installs'" :app-id="app.id" />
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { api } from "../lib/api";
import { useErrorText } from "../lib/errors";
import { LISTING_LOCALES } from "../lib/listing";
import OverviewPanel from "../components/editor/OverviewPanel.vue";
import VersionsPanel from "../components/editor/VersionsPanel.vue";
import ListingPanel from "../components/editor/ListingPanel.vue";
import DistributionPanel from "../components/editor/DistributionPanel.vue";
import CredentialsPanel from "../components/editor/CredentialsPanel.vue";
import TestersPanel from "../components/editor/TestersPanel.vue";
import UsagePanel from "../components/editor/UsagePanel.vue";
import InstallsPanel from "../components/editor/InstallsPanel.vue";

const TABS = ["overview", "versions", "listing", "distribution", "credentials", "testers", "usage", "installs"];

const props = defineProps({ id: { type: String, required: true } });
const { t, te } = useI18n();
const errorText = useErrorText();
const route = useRoute();
const router = useRouter();

const app = ref(null);
const versions = ref([]);
const credentials = ref([]);
const scopes = ref([]);
const listings = reactive({ en: null, ru: null });
const loading = ref(true);
const error = ref("");

const tab = computed(() => (TABS.includes(route.query.tab) ? route.query.tab : "versions"));
function setTab(key) {
  router.replace({ query: { ...route.query, tab: key } });
}

const BADGE = { published: "badge--success", private: "badge--brand", checks_failed: "badge--danger", blocked: "badge--danger", suspended: "badge--danger", submitted: "badge--info" };
const statusBadge = computed(() => BADGE[app.value?.status] || "");
function statusLabel(status) {
  return te(`apps.status.${status}`) ? t(`apps.status.${status}`) : status;
}

async function loadListing(locale) {
  try {
    listings[locale] = (await api.listing(props.id, locale)) || null;
  } catch (e) {
    if (e.status !== 404) throw e;
    listings[locale] = null;
  }
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const [detail, scopeList] = await Promise.all([
      api.app(props.id),
      scopes.value.length ? Promise.resolve(scopes.value) : api.scopes(),
      ...LISTING_LOCALES.map(loadListing)
    ]);
    app.value = detail?.app || detail;
    versions.value = detail?.versions || (await api.versions(props.id)) || [];
    credentials.value = detail?.credentials || (await api.credentials(props.id)) || [];
    scopes.value = scopeList || [];
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}

async function reloadCredentials() {
  try {
    credentials.value = (await api.credentials(props.id)) || [];
  } catch (e) {
    error.value = errorText(e);
  }
}

function onListingSaved(locale, listing) {
  listings[locale] = listing;
}

watch(() => props.id, load);
onMounted(load);
</script>
